import Immutable from 'seamless-immutable';
import contains from 'ramda/src/includes';
import isNil from 'ramda/src/isNil';
import pathOr from 'ramda/src/pathOr';
import { createSelector } from 'reselect';
import MetadataConstants from 'invision-core/src/components/metadata/metadata.constants';
import MetadataSelectors from 'invision-core/src/components/metadata/metadata.selectors';
import { createImmutableSelector } from 'invision-core/src/utilities/create.immutable.selector';
import { CreditCheckConfigurationSelector as CustomerCreditCheckConfigurationSelector, SelectedCustomerIsCreditCheckSkippedSelector, SelectedCustomerSelector, SelectedCustomerCreditCheckDetailsSelector } from './customer.selectors';
import { CREDIT_CHECK_STATUS, UPDATE_CLASSIFICATION_METHOD_STATUS } from '../constants/credit.check.constants';
import FaultCodes from '../../api/fault.code.keys';
import { SubmittedOrderErrorSelector } from './selected.offering.order.selectors';
import { addressStateRegionProvinceValueOptionsForCountry } from '../helpers/customer.selectors.helpers';
import { determineCreditClassificationStatus } from './credit.check.helper';
var IMMUTABLE_EMPTY_ARRAY = Immutable([]);
export var CreditCheckConfigurationSelector = CustomerCreditCheckConfigurationSelector;
export var IsCreditCheckSkippedSelector = SelectedCustomerIsCreditCheckSkippedSelector;
export var SubscriberCreditCheckDetailsSelector = SelectedCustomerCreditCheckDetailsSelector;
export var CreditCheckAdditionalPropertiesSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  return !isNil(config) && config.RequiredAdditionalProperties;
});
export var CanUserOverrideCreditCheckSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  return !isNil(config) && config.CanOverrideCreditCheckClassification;
});
export var CreditCheckClassificationOptionsSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  if (config && config.CreditClassifications) {
    return config.CreditClassifications.asMutable({
      deep: true
    }).map(function (item) {
      return {
        id: item.Id,
        text: item.Name,
        selected: false
      };
    });
  }
});
export var IsCreditCheckEnabledSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  return !isNil(config) && !isNil(config.CreditCheckNotEnabled) && !config.CreditCheckNotEnabled;
});
export var IsCreditCheckRequiredSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  return !isNil(config) && !!config.CreditCheckRequired;
});
export var IsMatchSubscriberCreditCheckDetailsSelector = createSelector([SelectedCustomerSelector], function (selectedCustomer) {
  var selectedCustomerId = pathOr(null, ['data', 'Id'], selectedCustomer);
  var creditCheckCustomerId = pathOr(null, ['creditCheck', 'Subscriber', 'Id'], selectedCustomer);
  return selectedCustomerId === creditCheckCustomerId;
});
export var SubscriberIsUpdatingCreditClassificationSelector = createSelector([SubscriberCreditCheckDetailsSelector], function (subscriberCreditCheck) {
  return subscriberCreditCheck.isUpdatingCreditClassification;
});
export var SubscriberCreditCheckStatusSelector = createSelector([SubscriberCreditCheckDetailsSelector, SubmittedOrderErrorSelector, SubscriberCreditCheckDetailsSelector], function (selectedCustomerCreditCheckDetails, submittedOrderLastAttemptError, subscriberCreditCheckDetails) {
  var editOfferErrorCode = pathOr(null, ['code'], submittedOrderLastAttemptError);
  var newConnectErrorCode = pathOr(null, ['error', 'code'], subscriberCreditCheckDetails);
  var errorCodesOfInterest = [FaultCodes.ORDER_SUBMITTED_CREDIT_CHECK_ERROR];
  if (editOfferErrorCode && contains(String(editOfferErrorCode), errorCodesOfInterest) || newConnectErrorCode && contains(String(newConnectErrorCode), errorCodesOfInterest)) {
    return CREDIT_CHECK_STATUS.ERROR;
  } else {
    var creditCheckClassification = pathOr(null, ['CreditCheckClassification'], selectedCustomerCreditCheckDetails);
    return determineCreditClassificationStatus(creditCheckClassification);
  }
});
export var SubscriberCreditCheckAddressStatesOptions = createSelector([SelectedCustomerSelector, SubscriberCreditCheckDetailsSelector, MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressCountry), MetadataSelectors.codes.MetadataCodeTypeSelector(MetadataConstants.codes.AddressStateProvinceRegion)], function (selectedCustomer, selectedCustomerCreditCheckDetails, addressCountries, addressStates) {
  var country = pathOr(null, ['Address', 'Country'], selectedCustomerCreditCheckDetails) || selectedCustomer.data.HomeCountry;
  if (country) {
    return addressStateRegionProvinceValueOptionsForCountry(country, addressCountries, addressStates);
  } else {
    return [];
  }
});
export var SubscriberCreditClassificationStatusSelector = createSelector([SubscriberCreditCheckDetailsSelector], function (selectedCustomerCreditCheckDetails) {
  var creditCheckClassification = pathOr(null, ['CreditCheckClassification'], selectedCustomerCreditCheckDetails);
  return determineCreditClassificationStatus(creditCheckClassification);
});
export var RunCreditCheckErrorMessageSelector = createImmutableSelector([SubscriberCreditCheckDetailsSelector], function (subscriberCreditCheckDetails) {
  return subscriberCreditCheckDetails.runCreditCheckErrorMessage;
});
export var IsCreditCheckSuccessSelector = createSelector([SubscriberCreditClassificationStatusSelector], function (subscriberCreditClassificationStatus) {
  return subscriberCreditClassificationStatus === CREDIT_CHECK_STATUS.SUCCESS;
});
export var MarkMandatoryCreditCheckFieldsDisabledSelector = createSelector([SubscriberCreditCheckDetailsSelector], function (selectedCustomerCreditCheckDetails) {
  var creditCheckClassificationMethod = pathOr(null, ['CreditCheckClassification', 'CreditCheckMethod'], selectedCustomerCreditCheckDetails);
  return creditCheckClassificationMethod === UPDATE_CLASSIFICATION_METHOD_STATUS.RUN_CREDIT_CHECK;
});
export var IsAddressMandatoryInCreditCheckSelector = createSelector([CreditCheckConfigurationSelector], function (config) {
  return !isNil(config) && config.RequiredSubscriberFields && config.RequiredSubscriberFields.includes('address');
});
export var CreditClassificationsSelector = createImmutableSelector([CreditCheckConfigurationSelector], function (config) {
  return pathOr(IMMUTABLE_EMPTY_ARRAY, ['CreditClassifications'], config);
});